import React, { useState, useEffect, useCallback } from 'react'
import { FiLogIn, FiLogOut, FiAlertCircle, FiRefreshCw, FiPlus, FiSettings } from 'react-icons/fi'
import { useLocation } from 'react-router-dom'
import MoodForm from '@/components/MoodForm'
import Header from '@/components/Header'
import Settings from '@/components/Settings'
import Player from './components/Player'
import Playlists from '@/components/Playlists'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useSpotify } from '@/hooks/useSpotify'
import { useGemini } from '@/hooks/useGemini'
import toast from 'react-hot-toast'

// Add constants for localStorage keys
const DEVICE_ID_KEY = 'spotify_device_id'
const WEBVIEW_DEVICE_ID_KEY = 'spotify_webview_device_id'

// Helper to detect if we're in a webview
const isWebView = () => {
  // User agent detection for common webview signatures
  const ua = navigator.userAgent.toLowerCase()
  
  // iOS WKWebView detection
  const isIOSWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(ua)
  
  // Android WebView detection
  const isAndroidWebView = ua.includes('wv') || 
    (ua.includes('android') && ua.includes('webview'))
  
  // Check for query parameter that can be passed when embedding
  const urlParams = new URLSearchParams(window.location.search)
  const isWebViewParam = urlParams.get('webview') === 'true'
  
  // Check for specific window properties that might indicate embedding
  const hasWindowParent = window !== window.parent
  
  return isIOSWebView || isAndroidWebView || isWebViewParam || hasWindowParent
}

// Get the appropriate device ID key based on environment
const getDeviceIdKey = () => {
  return isWebView() ? WEBVIEW_DEVICE_ID_KEY : DEVICE_ID_KEY
}

interface PlaylistOptions {
  trackCount: number
}

interface SpotifyTokens {
  accessToken: string
  refreshToken: string
  expiresAt: number
}

const App: React.FC = () => {
  // Get the URL search params to check for callback code
  const location = useLocation();
  
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [showCreatePlaylistModal, setShowCreatePlaylistModal] = useState(false)
  const [spotifyTokens, setSpotifyTokens] = useLocalStorage<SpotifyTokens | null>('spotify-tokens', null)
  const [geminiApiKey, setGeminiApiKey] = useLocalStorage<string>('gemini-api-key', '')
  const [isGenerating, setIsGenerating] = useState(false)
  const [currentMood, setCurrentMood] = useState<string | null>(null)
  const [playlistUri, setPlaylistUri] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPlaylistId, setCurrentPlaylistId] = useState<string | null>(null)
  const [selectedPlaylist, setSelectedPlaylist] = useState<string | null>(null)
  const [deviceId, setDeviceId] = useState<string | null>(() => {
    // Initialize from localStorage with the appropriate key
    const deviceIdKey = getDeviceIdKey()
    return localStorage.getItem(deviceIdKey)
  })
  const [shouldRefreshPlaylists, setShouldRefreshPlaylists] = useState(false)
  const [handlingCallback, setHandlingCallback] = useState(false)
  const [playlistOptions, setPlaylistOptions] = useLocalStorage<PlaylistOptions>('playlist-options', { 
    trackCount: 10, 
  })
  
  const { 
    isAuthenticated,
    spotifyFetch,
    generatePlaylist,
    handleSpotifyCallback,
    handleAuthCallback,
    connectToSpotify,
    isLoading: isSpotifyLoading,
    error: spotifyError,
  } = useSpotify(spotifyTokens, setSpotifyTokens)
  
  const {
    isConfigured,
    testApiKey,
  } = useGemini(geminiApiKey)
  
  // Check for Spotify callback code on initial render
  useEffect(() => {
    // Check if we have code and state in the URL (Spotify auth callback)
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    
    if (code && state && !handlingCallback) {
      processAuthCallback(code, state);
    }
  }, [location]);
  
  // Process the Spotify auth callback when returning to root URL
  const processAuthCallback = useCallback(async (code: string, state: string) => {
    try {
      // Process the authorization code
      setHandlingCallback(true);
      await handleAuthCallback(code, state);
      
      // Successfully authenticated
      toast.success('Successfully connected to Spotify!');
      
      // Clean URL to remove the callback parameters - replace without reloading
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // After successful login, trigger a playlist refresh
      setShouldRefreshPlaylists(true);
    } catch (error) {
      console.error('Error processing callback:', error);
      toast.error(`Authentication error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setHandlingCallback(false);
    }
  }, [handleAuthCallback]);
  
  // Persist player state when component mounts
  useEffect(() => {
    // Check if we have a saved playlist
    const savedState = localStorage.getItem('spotify_player_state')
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        if (state.playlistId) {
          setCurrentPlaylistId(state.playlistId)
          setSelectedPlaylist(state.playlistId)
        }
      } catch (e) {
        console.error('Error parsing saved player state', e)
      }
    }
  }, [])
  
  useEffect(() => {
    if (isAuthenticated && spotifyFetch && !localStorage.getItem('spotify_user_id')) {
      console.log('Fetching user ID...')
      spotifyFetch('https://api.spotify.com/v1/me')
        .then(data => {
          console.log('User data fetched successfully:', data.id)
          localStorage.setItem('spotify_user_id', data.id)
        })
        .catch(err => {
          console.error('Error fetching user data:', err)
        })
    }
  }, [isAuthenticated, spotifyFetch])
  
  useEffect(() => {
    // Check if we have a mood stored
    const storedMood = localStorage.getItem('current_mood')
    if (storedMood) {
      setCurrentMood(storedMood)
    }
  }, []);
  
  const handleRefreshPlaylists = useCallback(() => {
    setShouldRefreshPlaylists(true);
    toast.success('Refreshing your playlists...');
  }, []);
  
  const handleMoodSubmit = useCallback(async (mood: string, options?: Partial<PlaylistOptions>) => {
    localStorage.setItem('current_mood', mood)
    setCurrentMood(mood)
    
    // Update playlist options if provided
    if (options) {
      const updatedOptions: PlaylistOptions = {
        trackCount: options.trackCount || playlistOptions.trackCount
      };
      setPlaylistOptions(updatedOptions);
    }
    
    if (isAuthenticated) {
      // If user is already authenticated and we have Gemini API key, generate playlist
      if (isConfigured) {
        try {
          setIsGenerating(true);
          const playlistId = await generatePlaylist(mood, geminiApiKey, playlistOptions);
          
          if (playlistId) {
            toast.success('Playlist created successfully!');
            setCurrentPlaylistId(playlistId);
            setShouldRefreshPlaylists(true);
            // Close the create playlist modal
            setShowCreatePlaylistModal(false);
          } else {
            toast.error('Failed to create playlist');
          }
        } catch (error) {
          console.error('Error generating playlist:', error);
          toast.error((error as Error).message || 'Failed to create playlist');
        } finally {
          setIsGenerating(false);
        }
      } else {
        // If we don't have Gemini API key, show settings modal
        toast.error('Gemini API key is required to create playlists');
        setShowSettingsModal(true);
      }
    } else {
      // If not authenticated, start Spotify auth flow
      connectToSpotify();
    }
  }, [isAuthenticated, isConfigured, geminiApiKey, generatePlaylist, connectToSpotify, playlistOptions, setPlaylistOptions]);

  const handleApiKeySubmit = async (apiKey: string) => {
    const isValid = await testApiKey(apiKey)
    if (isValid) {
      setGeminiApiKey(apiKey)
      toast.success('API key configured successfully!')
      // Close settings modal after successful configuration
      setShowSettingsModal(false);
    } else {
      toast.error('Invalid API key')
    }
  }

  const handlePlay = (playlistId: string) => {
    console.log('Playing playlist:', playlistId);
    setCurrentPlaylistId(playlistId);
    setSelectedPlaylist(playlistId);
    
    // Save to localStorage for persistence
    localStorage.setItem('spotify_player_state', JSON.stringify({
      playlistId
    }));
    
    // Use the Player component for playback, not the API directly
    toast.success('Loading playlist...');
  }

  const handleDeletePlaylist = async (playlistId: string) => {
    if (!isAuthenticated || !spotifyFetch) {
      toast.error('You need to be logged in to delete playlists');
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('Handling playlist deletion for:', playlistId);
      
      // First verify the playlist exists and user can delete it
      const response = await spotifyFetch(`https://api.spotify.com/v1/playlists/${playlistId}`);
      const userId = localStorage.getItem('spotify_user_id');
      
      console.log('Checking ownership - Playlist owner:', response.owner.id, 'Current user:', userId);
      
      if (response.owner.id !== userId) {
        setIsLoading(false);
        toast.error("You can only delete playlists you've created");
        return;
      }
      
      // Use the deletePlaylist function from useSpotify hook
      await spotifyFetch(
        `https://api.spotify.com/v1/playlists/${playlistId}/followers`,
        {
          method: 'DELETE'
        }
      );
      
      // Clear the current playing playlist if it's the one being deleted
      if (currentPlaylistId === playlistId) {
        setCurrentPlaylistId(null);
      }
      
      toast.success('Playlist deleted successfully');
      
      // Trigger playlist refresh
      setShouldRefreshPlaylists(true);
    } catch (err: any) {
      console.error('Error deleting playlist:', err);
      toast.error(err.message || 'Failed to delete playlist');
    } finally {
      setIsLoading(false);
    }
  }

  const handleSpotifyLogout = () => {
    setSpotifyTokens(null)
    // Don't clear device ID to maintain it across sessions
    // This lets us reconnect to the same Spotify player without creating a new one
    const deviceIdKey = getDeviceIdKey()
    // Don't remove the device ID
    localStorage.removeItem('spotify_user_id')
    localStorage.removeItem('spotify_player_state')
    setCurrentPlaylistId(null)
    setSelectedPlaylist(null)
    toast.success('Logged out from Spotify')
  }

  const handleDeviceId = (id: string) => {
    console.log('Setting device ID:', id)
    // Always save to localStorage when setting, using the appropriate key
    if (id) {
      const deviceIdKey = getDeviceIdKey()
      console.log(`Storing device ID in ${deviceIdKey}:`, id)
      localStorage.setItem(deviceIdKey, id)
    }
    setDeviceId(id)
  }
  
  // Modal components
  const SettingsModal = () => {
    if (!showSettingsModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity animate-fadeIn">
        <div 
          className="bg-spotify-dark rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-spotify-border animate-modalSlideIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center p-5 border-b border-spotify-border">
            <h2 className="text-xl font-bold">Settings</h2>
            <button 
              onClick={() => setShowSettingsModal(false)}
              className="text-spotify-text-gray hover:text-white transition-colors rounded-full hover:bg-spotify-light p-1"
            >
              ✕
            </button>
          </div>
          <div className="p-5">
            <Settings
              accessToken={spotifyTokens?.accessToken || null}
              onApiKeySubmit={handleApiKeySubmit}
              onLogout={handleSpotifyLogout}
              apiKey={geminiApiKey}
              isConfigured={isConfigured}
              onConnect={connectToSpotify}
              isAuthenticated={isAuthenticated}
            />
          </div>
        </div>
      </div>
    );
  };
  
  const CreatePlaylistModal = () => {
    if (!showCreatePlaylistModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity animate-fadeIn">
        <div 
          className="bg-spotify-dark rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-spotify-border animate-modalSlideIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center p-5 border-b border-spotify-border">
            <h2 className="text-xl font-bold">Create New Playlist</h2>
            <button 
              onClick={() => setShowCreatePlaylistModal(false)}
              className="text-spotify-text-gray hover:text-white transition-colors rounded-full hover:bg-spotify-light p-1"
              disabled={isGenerating}
            >
              ✕
            </button>
          </div>
          {isGenerating ? (
            <div className="p-6 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-spotify-green mb-4"></div>
              <h3 className="text-spotify-text-light text-xl font-medium">Creating your playlist</h3>
              <p className="text-spotify-text-gray text-center mt-2">
                Using AI to find the perfect songs for your mood...
              </p>
              <div className="mt-6 w-full max-w-md">
                <div className="w-full h-1 bg-spotify-light rounded-full overflow-hidden">
                  <div className="h-full bg-spotify-green animate-pulse"></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5">
              <MoodForm 
                onSubmit={handleMoodSubmit} 
                currentMood={currentMood}
                isGenerating={isGenerating}
                defaultOptions={playlistOptions}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-spotify-dark text-white flex flex-col">
      <Header 
        isSpotifyConnected={isAuthenticated}
        isGeminiConfigured={isConfigured}
        onSettingsClick={() => setShowSettingsModal(true)}
        currentMood={currentMood}
        onResetMood={() => {
          localStorage.removeItem('current_mood')
          setCurrentMood(null)
        }}
      />
      
      <main className="flex-grow p-4 md:p-6 container mx-auto max-w-screen-xl overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-4 md:gap-6">
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">My Playlists</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleRefreshPlaylists}
                  className="bg-spotify-light p-2 rounded-full hover:bg-spotify-input transition-colors"
                  title="Refresh playlists"
                >
                  <FiRefreshCw />
                </button>
                <button
                  onClick={() => setShowCreatePlaylistModal(true)}
                  className="bg-spotify-green p-2 rounded-full hover:bg-spotify-green-bright transition-colors"
                  title="Create new playlist"
                >
                  <FiPlus />
                </button>
              </div>
            </div>
            
            <Playlists
              accessToken={spotifyTokens?.accessToken || null}
              onPlay={handlePlay}
              onDelete={handleDeletePlaylist}
              selectedPlaylistId={selectedPlaylist}
              shouldRefresh={shouldRefreshPlaylists}
              onRefreshDone={() => setShouldRefreshPlaylists(false)}
            />
          </div>
          
          <div className="mt-4 md:mt-0">
            <Player
              playlistId={currentPlaylistId}
              accessToken={spotifyTokens?.accessToken || null}
              deviceId={deviceId}
              onDeviceId={handleDeviceId}
            />
          </div>
        </div>
      </main>
      
      {/* Modals */}
      <SettingsModal />
      <CreatePlaylistModal />
    </div>
  )
}

export default App 